import {Controller} from "@hotwired/stimulus";
import {updateCart, updateWatchList} from "~/lib/badges";
import {putJson} from "~/lib/request";

// Add/remove a course (or course series) to the cart. One instance per button;
// rendered by CoursesHelper#html_options_for_cart_btn on the course tiles, the
// detail hero, the detail sidebar and the watch list.
//
// Replaces the jQuery init_cart_k()/sameCourseCartButtons() plumbing of carts.js.
// A course can carry several buttons on one page (hero + sidebar of the detail
// page), so every instance listens for cip:cart-changed and re-renders itself
// when the event carries its own uuid.
export default class extends Controller {
    static values = {
        courseUuid: String,
        courseRowUuid: String,
        addUrl: String,
        removeUrl: String,
        addTitle: String,
        removeTitle: String,
    };

    connect() {
        this.onCartChanged = this.onCartChanged.bind(this);
        document.addEventListener("cip:cart-changed", this.onCartChanged);
    }

    disconnect() {
        document.removeEventListener("cip:cart-changed", this.onCartChanged);
    }

    get uuid() {
        return this.courseUuidValue || this.courseRowUuidValue;
    }

    get inCart() {
        return this.element.classList.contains("cart-remove");
    }

    toggle() {
        if (this.pending) return;
        const adding = !this.inCart;
        const url = adding ? this.addUrlValue : this.removeUrlValue;
        if (!url) return;

        this.pending = true;
        putJson(url, {
            course_uuid: this.courseUuidValue,
            course_row_uuid: this.courseRowUuidValue,
            // the server drops the course from the watch list on every add
            from_watch: this.isWatchBooking ? true : undefined,
        }).then((data) => {
            updateCart(data.cart_count);
            // only the add response reports the watch count
            if (adding) updateWatchList(data.watch_count);
            document.dispatchEvent(new CustomEvent("cip:cart-changed", {
                detail: {uuid: this.uuid, added: adding, source: this.element},
            }));
        }).catch((error) => {
            // network error, 500, expired session, missing CSRF token: the
            // button keeps its state, but the user must not click into a void
            console.error("cart update failed", error);
            // hardcoded German like the other frontend controllers - the Vite
            // bundle has no gettext (see 15087-redesign-improvements.md, 4.4)
            window.alert("Das hat leider nicht geklappt. Bitte laden Sie die Seite neu und versuchen Sie es erneut.");
        }).finally(() => {
            this.pending = false;
        });
    }

    onCartChanged(event) {
        const {uuid, added, source} = event.detail;
        // without a uuid there is nothing to match on, so only the clicked button refreshes
        if (uuid ? uuid !== this.uuid : source !== this.element) return;

        // on the watch list the button moves the course cart-wards, so its row goes away.
        // only ever for the button that was actually clicked - never as a sibling sync.
        if (this.isWatchBooking) {
            if (source === this.element) this.element.closest("tr")?.remove();
            return;
        }
        this.render(added);
    }

    get isWatchBooking() {
        return this.element.classList.contains("watch-booking");
    }

    // the redesign SCSS keys everything off these two classes - the .when-add/.when-remove
    // text swap, the hero .cta-icon and the sidebar .btn-icon masks - so this class pair
    // is the contract, not an implementation detail.
    render(added) {
        this.element.classList.toggle("cart-remove", added);
        this.element.classList.toggle("cart-add", !added);
        this.element.title = added ? this.removeTitleValue : this.addTitleValue;

        // the watch list shows a font icon inside the button (the redesign tiles
        // use masked SVGs keyed off .cart-add/.cart-remove instead). There is no
        // "bag-remove" glyph - the plain bag is the in-cart state, as in
        // course-results.scss.
        const icon = this.element.querySelector("i");
        if (icon) {
            icon.classList.toggle("fa-shopping-bag", added);
            icon.classList.toggle("fa-shopping-bag-add", !added);
        }

        if (added) this.unwatch();
    }

    // adding to the cart removes the course from the watch list server-side
    // (CartsController#add), so the neighbouring heart has to follow.
    // .sidebar-actions is the detail page, .course-actions a search result tile -
    // the jQuery version only knew about the latter, so the detail page never synced.
    unwatch() {
        const actions = this.element.closest(".course-actions, .sidebar-actions");
        const watch = actions?.querySelector(".watch-remove");
        if (!watch) return;
        watch.classList.remove("watch-remove");
        watch.classList.add("watch-add");
    }
}
